import { getOrdersByClientId } from '#services/orders.js';
import { useEffect, useState, useMemo } from 'react';

const ESTADOS = ['', 'Pendiente', 'Aceptado', 'Rechazado'];

const OrderHistory = ({ user, isAuth }) => {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtros
  const [orderNumber, setOrderNumber] = useState(''); // texto
  const [status, setStatus] = useState(''); // select
  const [dateFrom, setDateFrom] = useState(''); // yyyy-mm-dd
  const [dateTo, setDateTo] = useState(''); // yyyy-mm-dd

  useEffect(() => {
    // Aquí podrías hacer una llamada a una API para obtener los datos de los pedidos
    // Por ahora, usaremos datos estáticos para ilustrar
    const fetchOrders = async () => {
      if (!isAuth || !user?.document_number) return;
      try {
        setLoading(true);
        setError('');
        const data = await getOrdersByClientId(user.document_number);
        setOrders(data);
      } catch (e) {
        console.error(e);
        setError('No pudimos cargar tu historial. Intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuth, user?.document_number]);

  // Formatear a COP
  const fmtCOP = (value) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(value ?? 0);

  // Aplicar filtros en memoria
  const filtered = useMemo(() => {
    if (!orders) return [];

    return orders.filter((o) => {
      // Número de orden (coincidencia parcial)
      if (
        orderNumber &&
        !o.Consecutivo.toLowerCase().includes(orderNumber.toLowerCase())
      ) {
        return false;
      }
      // Estado exacto (si hay uno seleccionado)
      if (status && o.Estado !== status) {
        return false;
      }
      // Rango de fechas (inclusive)
      if (dateFrom && o.Fecha < dateFrom) {
        return false;
      }
      if (dateTo && o.Fecha > dateTo) {
        return false;
      }
      return true;
    });
  }, [orders, orderNumber, status, dateFrom, dateTo]);

  return (
    <div className="section order-history">
      <div className="container">
        <div className="row">
          <div className="col col-100">
            <h2>Historial de pedidos</h2>
            <p>En esta sección podrás ver el historial de tus pedidos.</p>
          </div>
        </div>
        {/* Filtros */}
        <div className="row" style={{ marginTop: 12, marginBottom: 16 }}>
          <div className="col col-100">
            <div
              className="filters"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              <div className="filter-order">
                <label htmlFor="orderNumber" className="label">
                  N° de orden
                </label>
                <input
                  id="orderNumber"
                  type="text"
                  className="input"
                  placeholder="Ej: 100532"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                />
              </div>

              <div className="filter-order">
                <label htmlFor="status" className="label">
                  Estado
                </label>
                <select
                  id="status"
                  className="input"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {ESTADOS.map((s) => (
                    <option key={s || 'any'} value={s}>
                      {s || 'Todos'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-order">
                <label htmlFor="dateFrom" className="label">
                  Desde
                </label>
                <input
                  id="dateFrom"
                  type="date"
                  className="input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={dateTo || undefined}
                />
              </div>

              <div className="filter-order">
                <label htmlFor="dateTo" className="label">
                  Hasta
                </label>
                <input
                  id="dateTo"
                  type="date"
                  className="input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom || undefined}
                />
              </div>

              <div style={{ alignSelf: 'end' }}>
                <button
                  className="btn btn-blue"
                  onClick={() => {
                    setOrderNumber('');
                    setStatus('');
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  Limpiar filtros
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla / estados */}
        <div className="row">
          <div className="col col-100 overflow-mb-scroll">
            {loading ? (
              <div className="card">Cargando pedidos…</div>
            ) : error ? (
              <div className="card card-error">{error}</div>
            ) : (
              <table className="table-invoice-due table-order-history">
                <thead>
                  <tr>
                    <th>Número de pedido</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th>Total</th>
                    <th>Productos</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered && filtered.length > 0 ? (
                    filtered.map((order) => (
                      <tr key={order.ID}>
                        <td>{order.Consecutivo}</td>
                        <td>{order.Fecha}</td>
                        <td>
                          <EstadoPill value={order.Estado} />
                        </td>
                        <td>{fmtCOP(order.Total)}</td>
                        <td>
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {order.Items.map((p, idx) => (
                              <li
                                key={`${order.ID}-${idx}`}
                                style={{ listStyleType: 'none' }}
                              >
                                {p.Productos.Referencia} × {p.Cantidad}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>
                        No hay pedidos disponibles
                        {orders && orders.length > 0
                          ? ' con los filtros aplicados.'
                          : '.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Etiqueta visual para estados
function EstadoPill({ value }) {
  const color = (() => {
    switch (value) {
      case 'Pendiente':
        return '#f59e0b'; // ámbar
      case 'Aceptado':
        return '#10b981'; // verde
      case 'Rechazado':
        return '#ef4444'; // rojo
      default:
        return '#6b7280'; // gris
    }
  })();

  return (
    <span
      style={{
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {value}
    </span>
  );
}

export default OrderHistory;
